import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardMedia,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Breadcrumbs,
  Link,
  Skeleton,
  Alert,
  Snackbar,
  Divider,
  Stack,
  Badge,
  Paper,
  Dialog,
  DialogContent
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as ShoppingCartIcon,
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon,
  LocalOffer as LocalOfferIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { API_BASE_URL, authenticatedFetch } from '../config/api';
import apiTokenService from '../services/apiTokenService';
import PlaceholderImage from './PlaceholderImage';
import authService from '../services/authService';
import { trackViewContent, trackAddToCart } from '../services/metaPixel';

// Helper functions for cart management
const getCartItems = () => {
  const cartStr = localStorage.getItem("cart");
  if (!cartStr) return [];
  try {
    return JSON.parse(cartStr);
  } catch (error) {
    console.error("Error parsing cart data:", error);
    return [];
  }
};

const saveCartItems = (items) => {
  localStorage.setItem("cart", JSON.stringify(items));
  // Dispatch event to notify other components
  window.dispatchEvent(new CustomEvent('cartUpdated'));
};

// Wholesale cart helper functions
const getWholesaleCartItems = () => {
  const cartStr = localStorage.getItem("wholesaleCart");
  if (!cartStr) return [];
  try {
    return JSON.parse(cartStr);
  } catch (error) {
    console.error("Error parsing wholesale cart data:", error);
    return [];
  }
};

const saveWholesaleCartItems = (items) => {
  localStorage.setItem("wholesaleCart", JSON.stringify(items));
  // Dispatch event to notify other components
  
  // Dispatch the event immediately
  window.dispatchEvent(new CustomEvent('wholesaleCartUpdated', { 
    detail: { cartItems: items, cartLength: items.length }
  }));
  
  // Also dispatch after a short delay to ensure all components are ready
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent('wholesaleCartUpdated', { 
      detail: { cartItems: items, cartLength: items.length }
    }));
  }, 100);
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [cartItems, setCartItems] = useState(getCartItems());
  const [wholesaleCartItems, setWholesaleCartItems] = useState(getWholesaleCartItems()); // Add wholesale cart state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [suggestedLoading, setSuggestedLoading] = useState(false);
  const [wholesaleMode, setWholesaleMode] = useState(false); // Toggle between retail and wholesale
  const [wholesaleQuantity, setWholesaleQuantity] = useState(1); // For wholesale box quantity
  const [currentUser, setCurrentUser] = useState(authService.getCurrentUser()); // State for current user
  
  // Compute user role states based on current user state
  const isWholesaleUser = currentUser && currentUser.role === 'whole';
  const isCustomerUser = currentUser && currentUser.role === 'customer';
  const showSingleSales = !currentUser || isCustomerUser; // Show for guests or customers

  // Listen for user state changes (login/logout)
  useEffect(() => {
    const handleUserUpdate = () => {
      setCurrentUser(authService.getCurrentUser());
    };

    window.addEventListener('userUpdated', handleUserUpdate);
    
    return () => {
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  // Load product data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await apiTokenService.fetch(`${API_BASE_URL}/api/products.php?id=${id}`);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        
        if (data.error) {
          throw new Error(data.error);
        }
        
        if (data.product) {
          setProduct(data.product);
          
          // Track product view with Meta Pixel
          const price = data.product.after_discount && data.product.after_discount < data.product.sell_price 
            ? data.product.after_discount 
            : data.product.sell_price;
          
          trackViewContent({
            id: String(data.product.id),
            name: data.product.name,
            price: parseFloat(price) || 0
          });
          
          // Fetch suggested products after product is loaded
          if (data.product.category_id) {
            fetchSuggestedProducts(data.product.category_id);
          }
        } else {
          throw new Error('Product data not found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  // Fetch suggested products from the same category
  const fetchSuggestedProducts = async (categoryId) => {
    if (!categoryId) {
      return;
    }
    
    try {
      setSuggestedLoading(true);
      
      // Fetch all products with per_page=all parameter
      const response = await apiTokenService.fetch(`${API_BASE_URL}/api/products.php?per_page=all`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const responseText = await response.text();
      const data = JSON.parse(responseText);
      
      // Check if we have products array directly or inside success object
      const products = data.products || (data.success ? data.products : []);
      
      if (products && products.length > 0) {
        // Filter products by category and exclude current product
        // Convert both to strings or numbers for comparison
        const sameCategoryProducts = products.filter(p => {
          const matchesCategory = String(p.category_id) === String(categoryId);
          const isDifferentProduct = String(p.id) !== String(id);
          
          // For wholesale users, only show products with wholesale price > 0
          const hasWholesalePrice = isWholesaleUser ? 
            (p.wholesale && parseFloat(p.wholesale) > 0) : true;
          
          return matchesCategory && isDifferentProduct && hasWholesalePrice;
        });
        
        setSuggestedProducts(sameCategoryProducts.slice(0, 6)); // Limit to 6 products
      } else {
        setSuggestedProducts([]);
      }
    } catch (err) {
      console.error('Error fetching suggested products:', err);
      setSuggestedProducts([]);
    } finally {
      setSuggestedLoading(false);
    }
  };

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = () => {
      setCartItems(getCartItems());
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  // Listen for wholesale cart updates
  useEffect(() => {
    const handleWholesaleCartUpdate = () => {
      setWholesaleCartItems(getWholesaleCartItems());
    };

    window.addEventListener('wholesaleCartUpdated', handleWholesaleCartUpdate);
    return () => {
      window.removeEventListener('wholesaleCartUpdated', handleWholesaleCartUpdate);
    };
  }, []);

  const showMessage = (msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  const addToCart = () => {
    if (!product) return;

    const cartItem = {
      id: product.id,
      name: product.name,
      price: product.after_discount && product.after_discount < product.sell_price 
        ? product.after_discount 
        : product.sell_price,
      image: product.image && product.image.trim() ? 
        (product.image.startsWith('http') ? 
          product.image : 
          product.image.startsWith('api/uploads/products/') ? 
            `https://al-sadik.com/${product.image}` : 
            `https://al-sadik.com/api/uploads/products/${product.image}`
        ) : null,
      sku: product.sku,
      weight: product.weight || 0, // Include weight for delivery calculation
      quantity: quantity
    };

    const existingItems = getCartItems();
    const existingItemIndex = existingItems.findIndex(item => item.id === product.id);

    if (existingItemIndex >= 0) {
      // Update quantity if item already exists
      existingItems[existingItemIndex].quantity += quantity;
    } else {
      // Add new item
      existingItems.push(cartItem);
    }

    saveCartItems(existingItems);
    setCartItems(existingItems);
    
    // Track add to cart with Meta Pixel - ensuring value is numeric
    const trackingPrice = parseFloat(cartItem.price) || 0;
    trackAddToCart({
      id: String(product.id),
      name: product.name,
      price: trackingPrice
    });
    
    showMessage(`تم إضافة ${quantity} قطعة من ${product.name} إلى السلة`);
  };

  const addToWholesaleCart = () => {
    if (!product) return;

    const finalQuantity = wholesaleMode ? wholesaleQuantity * (product.quantity_per_box || 1) : quantity;
    
    const existingItems = getWholesaleCartItems();
    const existingItemIndex = existingItems.findIndex(item => 
      (item.id && item.id === product.id) || 
      (item.product_id && item.product_id === product.id)
    );

    let newCart;

    if (existingItemIndex > -1) {
      newCart = existingItems.map((item, index) =>
        index === existingItemIndex
          ? { ...item, quantity: item.quantity + finalQuantity }
          : item
      );
    } else {
      const cartItem = {
        id: product.id,
        product_id: product.id, // Add both for compatibility
        name: product.name,
        price: parseFloat(product.wholesale || product.sell_price), // Use wholesale price
        image: product.image && product.image.trim() ? 
          (product.image.startsWith('http') ? 
            product.image : 
            product.image.startsWith('api/uploads/products/') ? 
              `https://al-sadik.com/${product.image}` : 
              `https://al-sadik.com/api/uploads/products/${product.image}`
          ) : null,
        sku: product.sku || product.SKU || product.product_sku || '',
        weight: product.weight || 0,
        quantity: finalQuantity,
        brand: product.brand_name || product.brand || '',
        brand_id: product.brand_id || '',
        brand_name: product.brand_name || product.brand || '', // Add explicit brand_name field for backend
        type: 'wholesale',
        isWholesale: true,
        wholesaleBoxes: wholesaleMode ? wholesaleQuantity : 0,
        quantityPerBox: product.quantity_per_box || 1
      };

      newCart = [...existingItems, cartItem];
    }

    saveWholesaleCartItems(newCart);
    
    // Track wholesale add to cart with Meta Pixel - ensuring value is numeric
    const trackingPrice = parseFloat(product.wholesale || product.sell_price) || 0;
    trackAddToCart({
      id: String(product.id),
      name: product.name,
      price: trackingPrice
    });
    
    const modeText = wholesaleMode ? `${wholesaleQuantity} صندوق (${finalQuantity} قطعة)` : `${finalQuantity} قطعة`;
    showMessage(`تم إضافة ${modeText} من ${product.name} إلى سلة البيع بالجملة`);
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  const handleWholesaleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1) {
      setWholesaleQuantity(newQuantity);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.brand_name ? `${product.brand_name} - ${product.name}` : product.name,
          text: `تحقق من هذا المنتج: ${product.brand_name ? `${product.brand_name} - ${product.name}` : product.name}`,
          url: window.location.href,
        });
      } catch (err) {
        // Silently handle sharing cancellation
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      showMessage('تم نسخ رابط المنتج');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" width="100%" height={400} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="text" sx={{ fontSize: '2rem', mb: 2 }} />
            <Skeleton variant="text" sx={{ fontSize: '1rem', mb: 1 }} />
            <Skeleton variant="text" sx={{ fontSize: '1rem', mb: 1 }} />
            <Skeleton variant="text" sx={{ fontSize: '1rem', mb: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={60} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          خطأ في تحميل المنتج: {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/store')}
        >
          العودة للمتجر
        </Button>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          المنتج غير موجود
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/store')}
        >
          العودة للمتجر
        </Button>
      </Container>
    );
  }

  const hasDiscount = product.after_discount && product.after_discount < product.sell_price;
  // For wholesale users, don't show retail discounts
  const showDiscount = hasDiscount && !isWholesaleUser;
  // Use wholesale price for wholesale users, otherwise use regular pricing logic
  const currentPrice = isWholesaleUser && product.wholesale 
    ? product.wholesale 
    : (hasDiscount ? product.after_discount : product.sell_price);
  const imageUrl = product.image && product.image.trim() ? 
    (product.image.startsWith('http') ? 
      product.image : 
      product.image.startsWith('api/uploads/products/') ? 
        `https://al-sadik.com/${product.image}` : 
        `https://al-sadik.com/api/uploads/products/${product.image}`
    ) : null;

  // Generate JSON-LD structured data for the product
  const generateProductJsonLd = () => {
    if (!product) return null;
    
    // Ensure we have valid price data
    const price = currentPrice || product.sell_price || product.wholesale || 0;
    const hasValidPrice = price && price > 0;
    
    // Determine availability with fallback
    const availability = (product.quantity && product.quantity > 0) 
      ? "https://schema.org/InStock" 
      : "https://schema.org/OutOfStock";
    
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.brand_name ? `${product.brand_name} - ${product.name}` : (product.name || "منتج"),
      "description": product.description || product.name || "منتج من متجر الصادق",
      "productID": product.id ? product.id.toString() : "",
      "sku": product.sku || product.id?.toString() || "",
      "brand": {
        "@type": "Brand",
        "name": product.brand_name || product.brand || "الصادق",
        ...(product.brand_logo && product.brand_logo.trim() && {
          "logo": product.brand_logo.startsWith('http') ? 
            product.brand_logo : 
            product.brand_logo.startsWith('api/uploads/') ? 
              `https://al-sadik.com/${product.brand_logo}` : 
              `https://al-sadik.com/api/uploads/brands/${product.brand_logo}`
        })
      },
      "image": imageUrl || '',
      "url": window.location.href
    };

    // Always include offers - this fixes the Google Search Console error
    if (hasValidPrice) {
      jsonLd.offers = {
        "@type": "Offer",
        "price": price.toString(),
        "priceCurrency": "IQD", // Iraqi Dinar
        "availability": availability,
        "itemCondition": "https://schema.org/NewCondition",
        "seller": {
          "@type": "Organization",
          "name": "متجر الصادق"
        }
      };
    } else {
      // If no valid price, still include offers with availability
      jsonLd.offers = {
        "@type": "Offer",
        "availability": availability,
        "itemCondition": "https://schema.org/NewCondition",
        "seller": {
          "@type": "Organization",
          "name": "متجر الصادق"
        }
      };
    }

    // Add category if available
    if (product.category_name) {
      jsonLd.category = product.category_name;
    }

    // Add additional property for grouping
    jsonLd.additionalProperty = [{
      "@type": "PropertyValue",
      "propertyID": "item_group_id",
      "value": `category_${product.category_id || 'general'}`
    }];

    // Add aggregate rating if we have stock (placeholder for future reviews)
    if (product.quantity > 0) {
      jsonLd.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": "4.5",
        "reviewCount": "1"
      };
    }

    return jsonLd;
  };

  return (
    <Box>
      {/* JSON-LD Structured Data */}
      {product && (
        <script 
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateProductJsonLd())
          }}
        />
      )}
      
      {/* Mobile Layout */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {/* Full-width Product Image */}
        <Box sx={{ 
          position: 'relative', 
          width: '100%', 
          height: '40vh', 
          minHeight: 300,
          overflow: 'hidden',
          mt: { xs: 7, sm: 8 } // Add top margin to account for header
        }}>
          {showDiscount && (
            <Chip
              icon={<LocalOfferIcon />}
              label={`خصم ${Math.round(((product.sell_price - product.after_discount) / product.sell_price) * 100)}%`}
              color="error"
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 2,
                fontWeight: 'bold',
                fontSize: '0.9rem',
                px: 1,
                py: 0.5
              }}
            />
          )}
          
          {/* Back Button */}
          <IconButton
            onClick={() => navigate('/store')}
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              zIndex: 2,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          {/* Share Button */}
          <IconButton
            onClick={handleShare}
            sx={{
              position: 'absolute',
              top: 16,
              left: 70,
              zIndex: 2,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
            }}
          >
            <ShareIcon />
          </IconButton>

          <Box
            component="img"
            src={imageUrl}
            alt={product.name}
            onClick={() => setImageModalOpen(true)}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              backgroundColor: '#f5f5f5',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.9,
                transition: 'opacity 0.3s ease'
              }
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              // Create placeholder element without innerHTML to prevent layout thrashing
              const placeholder = document.createElement('div');
              placeholder.style.cssText = `
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                background-color: #f5f5f5;
                border: 1px dashed #ccc;
                height: 100%;
                color: #999;
              `;
              
              // Create text elements
              const iconDiv = document.createElement('div');
              iconDiv.style.cssText = 'font-size: 48px; margin-bottom: 8px;';
              iconDiv.textContent = '📷';
              
              const textDiv = document.createElement('div');
              textDiv.style.cssText = 'font-size: 14px;';
              textDiv.textContent = 'لا توجد صورة متاحة';
              
              placeholder.appendChild(iconDiv);
              placeholder.appendChild(textDiv);
              
              // Replace the content without innerHTML
              const parent = e.target.parentNode;
              parent.removeChild(e.target);
              parent.appendChild(placeholder);
            }}
          />
        </Box>

        {/* Product Information */}
        <Container sx={{ py: 2 }}>
          {/* Product Name */}
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold', 
              mb: 1,
              lineHeight: 1.3
            }}
          >
            {product.brand_name ? `${product.brand_name} - ${product.name}` : product.name}
          </Typography>

          {/* Brand and Category Tags */}
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            {product.brand_name && product.brand_logo && product.brand_logo.trim() && (
              <Box
                component="img"
                src={product.brand_logo.startsWith('http') ? 
                  product.brand_logo : 
                  product.brand_logo.startsWith('api/uploads/') ? 
                    `https://al-sadik.com/${product.brand_logo}` : 
                    `https://al-sadik.com/api/uploads/brands/${product.brand_logo}`
                }
                alt={product.brand_name}
                onClick={() => {
                  // Navigate to wholesale store for wholesale users, regular store for others
                  const targetStore = isWholesaleUser ? '/wholesale' : '/store';
                  navigate(`${targetStore}?brand_id=${product.brand_id}`);
                }}
                sx={{ 
                  width: 36, 
                  height: 36, 
                  objectFit: 'contain',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    transition: 'all 0.2s ease'
                  }
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            {product.category_name && (
              <Chip
                label={product.category_name}
                color="secondary"
                variant="filled"
                clickable
                onClick={() => {
                  // Navigate to wholesale store for wholesale users, regular store for others
                  const targetStore = isWholesaleUser ? '/wholesale' : '/store';
                  navigate(`${targetStore}?category_id=${product.category_id}`);
                }}
                sx={{
                  fontWeight: 'medium',
                  px: 1.5,
                  py: 1.5,
                  '& .MuiChip-label': {
                    fontSize: '1rem',
                    fontWeight: 'medium'
                  },
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                    transform: 'scale(1.05)',
                    transition: 'all 0.2s ease'
                  }
                }}
              />
            )}
          </Stack>

          {/* Price Section */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                {Number(currentPrice).toLocaleString()} د.ع
              </Typography>
              {showDiscount && (
                <Typography
                  variant="h6"
                  sx={{
                    textDecoration: 'line-through',
                    color: 'text.secondary'
                  }}
                >
                  {Number(product.sell_price).toLocaleString()} د.ع
                </Typography>
              )}
            </Stack>
            {showDiscount && (
              <Typography variant="body2" color="success.main">
                وفر {Number(product.sell_price - product.after_discount).toLocaleString()} د.ع
              </Typography>
            )}
            
            {/* SKU Display */}
            {product.sku && (
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 1,
                  fontSize: '0.9rem',
                  fontFamily: 'monospace',
                  letterSpacing: '0.5px'
                }}
              >
                كود المنتج: {product.sku}
              </Typography>
            )}
          </Box>

          {/* Wholesale Options */}
          {isWholesaleUser && (
            <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#f0f8ff', border: '2px solid #3b5dac' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#3b5dac' }}>
                خيارات البيع بالجملة
              </Typography>
              
              {/* Mode Toggle */}
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button
                  variant={!wholesaleMode ? 'contained' : 'outlined'}
                  onClick={() => setWholesaleMode(false)}
                  size="small"
                  sx={{ flex: 1 }}
                >
                  قطعة واحدة
                </Button>
                <Button
                  variant={wholesaleMode ? 'contained' : 'outlined'}
                  onClick={() => setWholesaleMode(true)}
                  size="small"
                  sx={{ flex: 1 }}
                >
                  كارتون ({product.quantity_per_box || 1})
                </Button>
              </Stack>

              {/* Wholesale Info */}
              {product.wholesale && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    سعر الجملة: {Number(product.wholesale).toLocaleString()} د.ع
                  </Typography>
                  {product.quantity_per_box && (
                    <Typography variant="body2" color="text.secondary">
                      الكمية لكل صندوق: {product.quantity_per_box} قطعة
                    </Typography>
                  )}
                </Box>
              )}

              {/* Wholesale Quantity Control */}
              {wholesaleMode ? (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    عدد الصناديق:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'white' }}>
                    <IconButton
                      onClick={() => handleWholesaleQuantityChange(wholesaleQuantity - 1)}
                      disabled={wholesaleQuantity <= 1}
                      size="small"
                    >
                      <RemoveIcon />
                    </IconButton>
                    <Typography sx={{ px: 2, py: 1, minWidth: 40, textAlign: 'center', fontWeight: 'bold' }}>
                      {wholesaleQuantity}
                    </Typography>
                    <IconButton 
                      onClick={() => handleWholesaleQuantityChange(wholesaleQuantity + 1)}
                      size="small"
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                </Stack>
              ) : (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    الكمية:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'white' }}>
                    <IconButton
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 1}
                      size="small"
                    >
                      <RemoveIcon />
                    </IconButton>
                    <Typography sx={{ px: 2, py: 1, minWidth: 40, textAlign: 'center', fontWeight: 'bold' }}>
                      {quantity}
                    </Typography>
                    <IconButton 
                      onClick={() => handleQuantityChange(quantity + 1)}
                      size="small"
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                </Stack>
              )}

              {/* Wholesale Total */}
              <Box sx={{ 
                p: 1.5, 
                bgcolor: '#e3f2fd', 
                borderRadius: 1, 
                mb: 2,
                textAlign: 'center'
              }}>
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                  {wholesaleMode 
                    ? `المجموع: ${((product.wholesale || product.sell_price) * wholesaleQuantity * (product.quantity_per_box || 1)).toLocaleString()} د.ع (${wholesaleQuantity * (product.quantity_per_box || 1)} قطعة)`
                    : `المجموع: ${((product.wholesale || product.sell_price) * quantity).toLocaleString()} د.ع`
                  }
                </Typography>
              </Box>

              {/* Wholesale Add to Cart Button */}
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<ShoppingCartIcon />}
                onClick={addToWholesaleCart}
                disabled={!product.wholesale || parseFloat(product.wholesale) <= 0}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  mb: 1,
                  backgroundColor: '#3b5dac',
                  '&:hover': {
                    backgroundColor: '#2a4490',
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    color: '#999',
                  }
                }}
              >
                {!product.wholesale || parseFloat(product.wholesale) <= 0 
                  ? 'غير متوفر للجملة' 
                  : 'إضافة لسلة الجملة'
                }
              </Button>
            </Paper>
          )}

          {/* Add to Cart and Quantity Section */}
          {showSingleSales && (
            <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              {/* Quantity Control */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  الكمية:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <IconButton
                    onClick={() => handleQuantityChange(quantity - 1)}
                    disabled={quantity <= 1}
                    size="small"
                  >
                    <RemoveIcon />
                  </IconButton>
                  <Typography sx={{ px: 2, py: 1, minWidth: 40, textAlign: 'center', fontWeight: 'bold' }}>
                    {quantity}
                  </Typography>
                  <IconButton 
                    onClick={() => handleQuantityChange(quantity + 1)}
                    size="small"
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              </Stack>

              {/* Total Price */}
              <Box sx={{ 
                p: 1.5, 
                bgcolor: 'primary.50', 
                borderRadius: 1, 
                mb: 2,
                textAlign: 'center'
              }}>
                <Typography variant="h5" color="primary.main" sx={{ fontWeight: 'bold' }}>
                  المجموع: {(currentPrice * quantity).toLocaleString()} د.ع
                </Typography>
              </Box>

              {/* Buttons Side by Side */}
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ShoppingCartIcon />}
                  onClick={addToCart}
                  sx={{
                    flex: 1 ,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    borderRadius: 2
                  }}
                >
                  إضافة للسلة
                </Button>

                <Button
                  variant="contained"
                  onClick={() => {
                    addToCart();
                    navigate('/checkout');
                  }}
                  sx={{ 
                    flex: 1,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    backgroundColor: 'success.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'success.dark',
                      color: 'white'
                    }
                  }}
                >
                  شراء مباشر
                </Button>
              </Stack>
            </Paper>
          )}

          {/* Description - iOS Style */}
          {product.description && (
            <Card 
              elevation={0}
              sx={{ 
                mb: 3, 
                borderRadius: 3,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                  textAlign: 'center'
                }}
              >
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: '1.3rem'
                  }}
                >
                  وصف المنتج
                </Typography>
              </Box>
              
              <Box sx={{ p: 2.5 }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    lineHeight: 1.7,
                    color: 'text.secondary',
                    fontSize: '0.95rem',
                    fontWeight: 400,
                    textAlign: 'justify',
                    wordSpacing: '0.05em',
                    letterSpacing: '0.01em',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {product.description}
                </Typography>
              </Box>
            </Card>
          )}

          {/* Delivery and Authenticity Notes */}
          <Card 
            elevation={4}
            sx={{ 
              mb: 3,
              borderRadius: 3,
              overflow: 'hidden',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fafb 100%)',
              border: '1px solid rgba(25, 118, 210, 0.12)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)',
              }
            }}
          >
            <Box sx={{ p: 3 }}>
             
              
              <Stack spacing={2}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'success.50',
                  border: '1px solid',
                  borderColor: 'success.200',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'success.100',
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                  }
                }}>
                  <Box sx={{
                    p: 0.5,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    mr: 2,
                    mt: 0.5,
                    minWidth: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography sx={{ fontSize: '1rem', color: 'white' }}>🚚</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ 
                      color: 'success.dark', 
                      fontWeight: 600,
                      mb: 0.5
                    }}>
                      توصيل لجميع المحافظات
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'success.700' }}>
                      خدمة توصيل سريعة وآمنة
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'primary.50',
                  border: '1px solid',
                  borderColor: 'primary.200',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'primary.100',
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                  }
                }}>
                  <Box sx={{
                    p: 0.5,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    mr: 2,
                    mt: 0.5,
                    minWidth: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography sx={{ fontSize: '1rem', color: 'white' }}>✅</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ 
                      color: 'primary.dark', 
                      fontWeight: 600,
                      mb: 0.5
                    }}>
                      منتج اصلي
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'primary.700' }}>
                      ضمان الأصالة والجودة
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'secondary.50',
                  border: '1px solid',
                  borderColor: 'secondary.200',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'secondary.100',
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                  }
                }}>
                  <Box sx={{
                    p: 0.5,
                    borderRadius: '50%',
                    bgcolor: 'secondary.main',
                    mr: 2,
                    mt: 0.5,
                    minWidth: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography sx={{ fontSize: '1rem', color: 'white' }}>🔍</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ 
                      color: 'secondary.dark', 
                      fontWeight: 600,
                      mb: 0.5
                    }}>
                      يفحص المنتج بحضور المندوب
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'secondary.700' }}>
                      فحص التشغيل قبل الاستلام
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Card>

          {/* Additional Product Info */}
          {product.barcode && (
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                الباركود: {product.barcode}
              </Typography>
            </Paper>
          )}
        </Container>
      </Box>

      {/* Desktop Layout */}
      <Container maxWidth="lg" sx={{ 
        mt: { xs: 2, md: 4 }, 
        mb: 4, 
        display: { xs: 'none', md: 'block' },
        pt: { md: 2 } // Add padding top for desktop to account for header
      }}>
        {/* Breadcrumb Navigation */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/')}
            sx={{ textDecoration: 'none' }}
          >
            الرئيسية
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/store')}
            sx={{ textDecoration: 'none' }}
          >
            المتجر
          </Link>
          <Typography variant="body2" color="text.primary">
            {product.brand_name ? `${product.brand_name} - ${product.name}` : product.name}
          </Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          {/* Product Image */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              position: 'relative',
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6,
                transition: 'all 0.3s ease'
              }
            }}>
              {showDiscount && (
                <Chip
                  icon={<LocalOfferIcon />}
                  label={`خصم ${Math.round(((product.sell_price - product.after_discount) / product.sell_price) * 100)}%`}
                  color="error"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 1,
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    px: 1,
                    py: 0.5
                  }}
                />
              )}
              <CardMedia
                component="img"
                image={imageUrl}
                alt={product.name}
                onClick={() => setImageModalOpen(true)}
                sx={{
                  height: { xs: 400, md: 500 },
                  objectFit: 'contain',
                  backgroundColor: '#f5f5f5',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)'
                  }
                }}
                onError={(e) => {
                  // Replace with placeholder on error without innerHTML
                  e.target.style.display = 'none';
                  
                  const placeholder = document.createElement('div');
                  placeholder.style.cssText = `
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background-color: #f5f5f5;
                    border: 1px dashed #ccc;
                    height: ${window.innerWidth < 768 ? '300px' : '400px'};
                    color: #999;
                  `;
                  
                  const iconDiv = document.createElement('div');
                  iconDiv.style.cssText = 'font-size: 48px; margin-bottom: 8px;';
                  iconDiv.textContent = '📷';
                  
                  const textDiv = document.createElement('div');
                  textDiv.style.cssText = 'font-size: 14px;';
                  textDiv.textContent = 'لا توجد صورة متاحة';
                  
                  placeholder.appendChild(iconDiv);
                  placeholder.appendChild(textDiv);
                  
                  const parent = e.target.parentNode;
                  parent.removeChild(e.target);
                  parent.appendChild(placeholder);
                }}
            />
          </Card>

          {/* Delivery and Authenticity Notes - Below Product Image */}
          <Card 
            elevation={6}
            sx={{ 
              mt: 3,
              borderRadius: 4,
              overflow: 'hidden',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fafb 100%)',
              border: '1px solid rgba(25, 118, 210, 0.12)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)',
              }
            }}
          >
            <Box sx={{ p: 4 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 4,
                pb: 3,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
              }}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: '50%',
                  bgcolor: 'primary.50',
                  mr: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography sx={{ fontSize: '2rem' }}>📋</Typography>
                </Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    fontSize: '1.4rem'
                  }}
                >
                  ضمانات وخدمات العملاء
                </Typography>
              </Box>
              
              <Stack spacing={3}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  p: 3,
                  borderRadius: 3,
                  bgcolor: 'success.50',
                  border: '1px solid',
                  borderColor: 'success.200',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    bgcolor: 'success.100',
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}>
                  <Box sx={{
                    p: 1,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    mr: 3,
                    mt: 0.5,
                    minWidth: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 2
                  }}>
                    <Typography sx={{ fontSize: '1.2rem', color: 'white' }}>🚚</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ 
                      color: 'success.dark', 
                      fontWeight: 600,
                      mb: 1
                    }}>
                      توصيل لجميع المحافظات
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'success.700' }}>
                      خدمة توصيل سريعة وآمنة لكافة المناطق
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  p: 3,
                  borderRadius: 3,
                  bgcolor: 'primary.50',
                  border: '1px solid',
                  borderColor: 'primary.200',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    bgcolor: 'primary.100',
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}>
                  <Box sx={{
                    p: 1,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    mr: 3,
                    mt: 0.5,
                    minWidth: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 2
                  }}>
                    <Typography sx={{ fontSize: '1.2rem', color: 'white' }}>✅</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ 
                      color: 'primary.dark', 
                      fontWeight: 600,
                      mb: 1
                    }}>
                      منتج اصلي
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'primary.700' }}>
                      ضمان الأصالة والجودة العالية
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  p: 3,
                  borderRadius: 3,
                  bgcolor: 'secondary.50',
                  border: '1px solid',
                  borderColor: 'secondary.200',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    bgcolor: 'secondary.100',
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}>
                  <Box sx={{
                    p: 1,
                    borderRadius: '50%',
                    bgcolor: 'secondary.main',
                    mr: 3,
                    mt: 0.5,
                    minWidth: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 2
                  }}>
                    <Typography sx={{ fontSize: '1.2rem', color: 'white' }}>🔍</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ 
                      color: 'secondary.dark', 
                      fontWeight: 600,
                      mb: 1
                    }}>
                      يفحص المنتج بحضور المندوب
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'secondary.700' }}>
                      فحص شامل للمنتج قبل الاستلام والدفع
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Card>
        </Grid>

        {/* Product Details */}
        <Grid item xs={12} md={6}>
          <Box sx={{ position: 'sticky', top: 80 }}>
            {/* Product Title */}
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {product.brand_name ? `${product.brand_name} - ${product.name}` : product.name}
            </Typography>

            {/* Brand and Category Tags */}
            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              {product.sku && (
                <Chip label={`SKU: ${product.sku}`} variant="outlined" size="small" />
              )}
              {product.brand_name && product.brand_logo && product.brand_logo.trim() && (
                <Box
                  component="img"
                  src={product.brand_logo.startsWith('http') ? 
                    product.brand_logo : 
                    product.brand_logo.startsWith('api/uploads/') ? 
                      `https://al-sadik.com/${product.brand_logo}` : 
                      `https://al-sadik.com/api/uploads/brands/${product.brand_logo}`
                  }
                  alt={product.brand_name}
                  onClick={() => {
                    // Navigate to wholesale store for wholesale users, regular store for others
                    const targetStore = isWholesaleUser ? '/wholesale' : '/store';
                    navigate(`${targetStore}?brand_id=${product.brand_id}`);
                  }}
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    objectFit: 'contain',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      transition: 'all 0.2s ease'
                    }
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              {product.category_name && (
                <Chip 
                  label={product.category_name} 
                  color="secondary" 
                  size="medium"
                  clickable
                  onClick={() => {
                    // Navigate to wholesale store for wholesale users, regular store for others
                    const targetStore = isWholesaleUser ? '/wholesale' : '/store';
                    navigate(`${targetStore}?category_id=${product.category_id}`);
                  }}
                  sx={{
                    fontWeight: 'medium',
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: 'secondary.dark',
                      color: 'white',
                      transform: 'scale(1.05)',
                      transition: 'all 0.2s ease'
                    }
                  }}
                />
              )}
            </Stack>

            {/* Price */}
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                  {Number(currentPrice).toLocaleString()} د.ع
                </Typography>
                {showDiscount && (
                  <Typography
                    variant="h6"
                    sx={{
                      textDecoration: 'line-through',
                      color: 'text.secondary'
                    }}
                  >
                    {Number(product.sell_price).toLocaleString()} د.ع
                  </Typography>
                )}
              </Stack>
              {showDiscount && (
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  وفر {Number(product.sell_price - product.after_discount).toLocaleString()} د.ع
                </Typography>
              )}
            </Paper>

            {/* Wholesale Options for Desktop */}
            {isWholesaleUser && (
              <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: '#f0f8ff', border: '2px solid #3b5dac' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: '#3b5dac' }}>
                  خيارات البيع بالجملة
                </Typography>
                
                {/* Mode Toggle */}
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                  <Button
                    variant={!wholesaleMode ? 'contained' : 'outlined'}
                    onClick={() => setWholesaleMode(false)}
                    sx={{ flex: 1, py: 1.5 }}
                  >
                    قطعة واحدة
                  </Button>
                  <Button
                    variant={wholesaleMode ? 'contained' : 'outlined'}
                    onClick={() => setWholesaleMode(true)}
                    sx={{ flex: 1, py: 1.5 }}
                  >
                    كارتون ({product.quantity_per_box || 1}فطعة)
                  </Button>
                </Stack>

                {/* Wholesale Info */}
                {product.wholesale && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: 2, border: 1, borderColor: 'grey.300' }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>سعر الجملة:</strong> {Number(product.wholesale).toLocaleString()} د.ع
                    </Typography>
                    {product.quantity_per_box && (
                      <Typography variant="body1" color="text.secondary">
                        <strong>الكمية لكل صندوق:</strong> {product.quantity_per_box} قطعة
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Wholesale Quantity Control */}
                {wholesaleMode ? (
                  <Box sx={{ mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        عدد الصناديق:
                      </Typography>
                      <Paper elevation={1} sx={{ display: 'flex', alignItems: 'center', bgcolor: 'white' }}>
                        <IconButton
                          onClick={() => handleWholesaleQuantityChange(wholesaleQuantity - 1)}
                          disabled={wholesaleQuantity <= 1}
                          size="small"
                          sx={{ borderRadius: '8px 0 0 8px' }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography 
                          sx={{ 
                            px: 3, 
                            py: 1, 
                            minWidth: 50, 
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: '1.1rem'
                          }}
                        >
                          {wholesaleQuantity}
                        </Typography>
                        <IconButton 
                          onClick={() => handleWholesaleQuantityChange(wholesaleQuantity + 1)}
                          size="small"
                          sx={{ borderRadius: '0 8px 8px 0' }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    </Stack>
                  </Box>
                ) : (
                  <Box sx={{ mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        الكمية:
                      </Typography>
                      <Paper elevation={1} sx={{ display: 'flex', alignItems: 'center', bgcolor: 'white' }}>
                        <IconButton
                          onClick={() => handleQuantityChange(quantity - 1)}
                          disabled={quantity <= 1}
                          size="small"
                          sx={{ borderRadius: '8px 0 0 8px' }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography 
                          sx={{ 
                            px: 3, 
                            py: 1, 
                            minWidth: 50, 
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: '1.1rem'
                          }}
                        >
                          {quantity}
                        </Typography>
                        <IconButton 
                          onClick={() => handleQuantityChange(quantity + 1)}
                          size="small"
                          sx={{ borderRadius: '0 8px 8px 0' }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    </Stack>
                  </Box>
                )}

                {/* Wholesale Total Price */}
                <Box sx={{ 
                  p: 2, 
                  bgcolor: '#e3f2fd', 
                  borderRadius: 2, 
                  border: 1, 
                  borderColor: 'primary.200',
                  mb: 3
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" color="primary.dark" sx={{ fontWeight: 'medium' }}>
                      إجمالي سعر الجملة:
                    </Typography>
                    <Typography variant="h5" color="primary.main" sx={{ fontWeight: 'bold' }}>
                      {wholesaleMode 
                        ? `${((product.wholesale || product.sell_price) * wholesaleQuantity * (product.quantity_per_box || 1)).toLocaleString()} د.ع`
                        : `${((product.wholesale || product.sell_price) * quantity).toLocaleString()} د.ع`
                      }
                    </Typography>
                  </Stack>
                  {wholesaleMode && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                      ({wholesaleQuantity * (product.quantity_per_box || 1)} قطعة إجمالي)
                    </Typography>
                  )}
                </Box>

                {/* Wholesale Add to Cart Button */}
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<ShoppingCartIcon />}
                  onClick={addToWholesaleCart}
                  disabled={!product.wholesale || parseFloat(product.wholesale) <= 0}
                  sx={{
                    py: 2,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: '#3b5dac',
                    '&:hover': {
                      backgroundColor: '#2a4490',
                      boxShadow: 6,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.3s ease'
                    },
                    '&:disabled': {
                      backgroundColor: '#ccc',
                      color: '#999',
                      boxShadow: 'none',
                      transform: 'none',
                    }
                  }}
                >
                  {!product.wholesale || parseFloat(product.wholesale) <= 0 
                    ? 'غير متوفر للجملة' 
                    : 'إضافة لسلة الجملة'
                  }
                </Button>
              </Paper>
            )}

            {/* Action Buttons */}
            {showSingleSales && (
              <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                  خيارات الشراء
                </Typography>
                
                {/* Quantity and Total Price Row */}
                <Box sx={{ mb: 3 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      الكمية:
                    </Typography>
                    <Paper elevation={1} sx={{ display: 'flex', alignItems: 'center', bgcolor: 'white' }}>
                      <IconButton
                        onClick={() => handleQuantityChange(quantity - 1)}
                        disabled={quantity <= 1}
                        size="small"
                        sx={{ 
                          borderRadius: '8px 0 0 8px',
                          '&:disabled': { bgcolor: 'grey.100' }
                        }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography 
                        sx={{ 
                          px: 3, 
                          py: 1, 
                          minWidth: 50, 
                          textAlign: 'center',
                          fontWeight: 'bold',
                          fontSize: '1.1rem'
                        }}
                      >
                        {quantity}
                      </Typography>
                      <IconButton 
                        onClick={() => handleQuantityChange(quantity + 1)}
                        size="small"
                        sx={{ borderRadius: '0 8px 8px 0' }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  </Stack>
                  
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'primary.50', 
                    borderRadius: 2, 
                    border: 1, 
                    borderColor: 'primary.200' 
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" color="primary.dark" sx={{ fontWeight: 'medium' }}>
                        إجمالي السعر:
                      </Typography>
                      <Typography variant="h5" color="primary.main" sx={{ fontWeight: 'bold' }}>
                        {(currentPrice * quantity).toLocaleString()} د.ع
                      </Typography>
                    </Stack>
                  </Box>
                </Box>

                {/* Add to Cart Button */}
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<ShoppingCartIcon />}
                  onClick={addToCart}
                  sx={{
                    py: 2,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    boxShadow: 3,
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.3s ease'
                    },
                    mb: 2
                  }}
                >
                  إضافة للسلة
                </Button>
                
                {/* Secondary Actions */}
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      addToCart();
                      navigate('/checkout');
                    }}
                    sx={{ 
                      flex: 1,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 'medium',
                      backgroundColor: 'success.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'success.dark',
                        color: 'white',
                        transform: 'translateY(-1px)',
                        transition: 'all 0.2s ease'
                      }
                    }}
                  >
                    شراء مباشر
                  </Button>
                  <IconButton
                    onClick={handleShare}
                    sx={{ 
                      border: 2, 
                      borderColor: 'primary.main',
                      bgcolor: 'primary.50',
                      color: 'primary.main',
                      borderRadius: 2,
                      px: 2,
                      '&:hover': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        transform: 'translateY(-1px)',
                        transition: 'all 0.2s ease'
                      }
                    }}
                    title="مشاركة المنتج"
                  >
                    <ShareIcon />
                  </IconButton>
                </Stack>
              </Paper>
            )}

            {/* Product Info */}
            {(product.barcode || product.description) && (
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  معلومات المنتج
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Stack spacing={2}>
                  {product.barcode && (
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'grey.50', 
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'grey.200'
                    }}>
                      <Typography variant="body2" color="text.secondary" component="span" sx={{ fontWeight: 'medium' }}>
                        الباركود: 
                      </Typography>
                      <Typography variant="body2" component="span" sx={{ ml: 1, fontWeight: 'bold' }}>
                        {product.barcode}
                      </Typography>
                    </Box>
                  )}

                  {product.description && (
                    <Box sx={{ 
                      p: 3, 
                      bgcolor: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                      borderRadius: 2,
                      border: 1,
                      borderColor: '#e9ecef',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '3px',
                        background: 'linear-gradient(90deg, #2196F3, #21CBF3)',
                      }
                    }}>
                      <Typography 
                        variant="body1" 
                        color="primary.main" 
                        component="div" 
                        sx={{ 
                          fontWeight: 'bold',
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          '&::before': {
                            content: '"📝"',
                            fontSize: '1.1rem'
                          }
                        }}
                      >
                        وصف المنتج التفصيلي
                      </Typography>
                      <Typography 
                        variant="body1" 
                        component="div" 
                        sx={{ 
                          lineHeight: 1.8,
                          color: '#495057',
                          fontSize: '1rem',
                          fontWeight: 400,
                          textAlign: 'justify',
                          wordSpacing: '0.1em',
                          letterSpacing: '0.02em',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {product.description}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}
          </Box>
        </Grid>
      </Grid>
      </Container>

      {/* Suggested Products Section */}
      {product && (
        <Box sx={{ 
          bgcolor: '#f8f9fa', 
          py: { xs: 4, md: 6 }, 
          mt: { xs: 4, md: 6 } 
        }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 'bold', 
                mb: 3, 
                textAlign: 'center',
                color: '#2c3e50'
              }}
            >
              منتجات مشابهة
            </Typography>
            
            {suggestedLoading ? (
              <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <Card key={index} sx={{ minWidth: { xs: 200, sm: 240 }, flexShrink: 0 }}>
                    <Skeleton variant="rectangular" height={160} />
                    <Box sx={{ p: 2 }}>
                      <Skeleton variant="text" height={24} />
                      <Skeleton variant="text" height={20} />
                      <Skeleton variant="text" height={20} />
                    </Box>
                  </Card>
                ))}
              </Box>
            ) : suggestedProducts.length > 0 ? (
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: { xs: 1.5, sm: 2 },
                px: { xs: 1, sm: 0 },
              }}>
                {suggestedProducts.slice(0, 4).map((suggestedProduct) => {
                  const suggestionImageUrl = suggestedProduct.image && suggestedProduct.image.trim() ? 
                    (suggestedProduct.image.startsWith('http') ? 
                      suggestedProduct.image : 
                      suggestedProduct.image.startsWith('api/uploads/products/') ? 
                        `https://al-sadik.com/${suggestedProduct.image}` : 
                        `https://al-sadik.com/api/uploads/products/${suggestedProduct.image}`
                    ) : null;

                  const hasDiscount = suggestedProduct.after_discount && 
                    suggestedProduct.after_discount < suggestedProduct.sell_price;

                  return (
                    <Card 
                      key={suggestedProduct.id}
                      sx={{ 
                        width: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                        }
                      }}
                      onClick={() => navigate(`/product/${suggestedProduct.id}`)}
                    >
                      <Box sx={{ position: 'relative' }}>
                        {hasDiscount && (
                          <Chip
                            icon={<LocalOfferIcon />}
                            label={`خصم ${Math.round(((suggestedProduct.sell_price - suggestedProduct.after_discount) / suggestedProduct.sell_price) * 100)}%`}
                            color="error"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 6,
                              right: 6,
                              zIndex: 2,
                              fontWeight: 'bold',
                              fontSize: '0.65rem',
                              height: 20
                            }}
                          />
                        )}
                        
                        {suggestionImageUrl ? (
                          <CardMedia
                            component="img"
                            height="220"
                            image={suggestionImageUrl}
                            alt={suggestedProduct.name}
                            sx={{ 
                              objectFit: 'contain',
                              backgroundColor: '#f9f9f9',
                              padding: '4px',
                              borderRadius: '4px 4px 0 0'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const placeholder = e.target.parentNode.querySelector('.placeholder');
                              if (placeholder) placeholder.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        
                        <Box 
                          className="placeholder"
                          sx={{
                            display: suggestionImageUrl ? 'none' : 'flex',
                            height: 220,
                            backgroundColor: '#f5f5f5',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            color: '#999'
                          }}
                        >
                          <Typography variant="h4">📷</Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>لا توجد صورة</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ p: 1.5 }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 'bold',
                            mb: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '2.2em',
                            fontSize: '0.85rem',
                            lineHeight: 1.2
                          }}
                        >
                          {suggestedProduct.name}
                        </Typography>

                        {suggestedProduct.brand_name && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                              display: 'block', 
                              mb: 0.5,
                              fontSize: '0.7rem'
                            }}
                          >
                            {suggestedProduct.brand_name}
                          </Typography>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          {hasDiscount ? (
                            <>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 'bold', 
                                  color: '#e74c3c',
                                  fontSize: '0.85rem'
                                }}
                              >
                                {parseInt(suggestedProduct.after_discount).toLocaleString()} د.ع
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  textDecoration: 'line-through', 
                                  color: '#95a5a6',
                                  fontSize: '0.7rem'
                                }}
                              >
                                {parseInt(suggestedProduct.sell_price).toLocaleString()} د.ع
                              </Typography>
                            </>
                          ) : (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: '#2c3e50',
                                fontSize: '0.85rem'
                              }}
                            >
                              {parseInt(suggestedProduct.sell_price).toLocaleString()} د.ع
                            </Typography>
                          )}
                        </Box>

                        <Button
                          variant="contained"
                          size="small"
                          fullWidth
                          sx={{
                            backgroundColor: '#3b5dac',
                            color: 'white',
                            fontSize: '0.7rem',
                            py: 0.5,
                            minHeight: 'auto',
                            boxShadow: 'none',
                            '&:hover': {
                              backgroundColor: '#2a4490',
                              boxShadow: '0 2px 8px rgba(59, 93, 172, 0.3)',
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/product/${suggestedProduct.id}`);
                          }}
                        >
                          عرض التفاصيل
                        </Button>
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Typography 
                variant="body1" 
                sx={{ 
                  textAlign: 'center', 
                  color: '#666',
                  py: 4 
                }}
              >
                لا توجد منتجات مشابهة
              </Typography>
            )}
          </Container>
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Image Full View Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {/* Close Button */}
          <IconButton
            onClick={() => setImageModalOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 2,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              color: 'text.primary',
              '&:hover': { 
                bgcolor: 'rgba(255, 255, 255, 1)',
                transform: 'scale(1.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          
          {/* Full Size Image */}
          <Box
            component="img"
            src={imageUrl}
            alt={product.name}
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: '90vh',
              objectFit: 'contain',
              backgroundColor: '#000',
              borderRadius: 2
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              
              const placeholder = document.createElement('div');
              placeholder.style.cssText = `
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                background-color: #f5f5f5;
                border: 1px dashed #ccc;
                height: 400px;
                color: #999;
                border-radius: 8px;
              `;
              
              const iconDiv = document.createElement('div');
              iconDiv.style.cssText = 'font-size: 48px; margin-bottom: 8px;';
              iconDiv.textContent = '📷';
              
              const textDiv = document.createElement('div');
              textDiv.style.cssText = 'font-size: 16px;';
              textDiv.textContent = 'لا توجد صورة متاحة';
              
              placeholder.appendChild(iconDiv);
              placeholder.appendChild(textDiv);
              
              const parent = e.target.parentNode;
              parent.removeChild(e.target);
              parent.appendChild(placeholder);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProductDetail;
